// frontend-react/src/components/ValidationModal.tsx
import React, { useState } from 'react';
import { Modal, Upload, Button, Spin, Alert, Space } from 'antd';
import {
  InboxOutlined,
  DownloadOutlined,
  PaperClipOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import type { ReviewItem } from '../types';
import { evaluateAccuracy, downloadValidationFile } from '../services/validationService';
import type { ValidationResult } from '../services/validationService';

const { Dragger } = Upload;

type Step = 'idle' | 'loading' | 'done' | 'error';

interface ValidationModalProps {
  visible: boolean;
  activeResults: ReviewItem[];
  onClose: () => void;
  fileName?: string | undefined;
}

const BTN_STYLE: React.CSSProperties = {
  background: '#6B7B5A',
  borderColor: '#6B7B5A',
  color: '#fff',
  height: 40,
};

const ValidationModal: React.FC<ValidationModalProps> = ({
  visible,
  activeResults,
  onClose,
  fileName,
}) => {
  const [step, setStep] = useState<Step>('idle');
  const [docxFile, setDocxFile] = useState<File | null>(null);
  const [result, setResult] = useState<ValidationResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const reset = () => {
    setStep('idle');
    setDocxFile(null);
    setResult(null);
    setErrorMessage('');
  };

  const handleClose = () => {
    if (step === 'loading') return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!docxFile) return;
    setStep('loading');
    try {
      const r = await evaluateAccuracy(docxFile, activeResults);
      setResult(r);
      setStep('done');
    } catch (err: any) {
      setErrorMessage(err?.response?.data?.detail || err?.message || '驗測失敗，請稍後再試');
      setStep('error');
    }
  };

  const handleDownload = async (fileType: 'word' | 'excel') => {
    if (!result) return;
    const stem = fileName ? fileName.replace(/\.(pdf|docx)$/i, '') : 'report';
    const ext = fileType === 'word' ? 'docx' : 'xlsx';
    const name = `validation_${stem}.${ext}`;
    try {
      await downloadValidationFile(result.task_id, fileType, name);
    } catch {
      // 單次下載失敗不影響另一個
    }
  };

  const renderBody = () => {
    switch (step) {
      case 'idle':
        return (
          <Space direction='vertical' style={{ width: '100%' }} size='middle'>
            {/* 上傳區：有檔案時隱藏，改顯示已選檔案列 */}
            {!docxFile ? (
              <Dragger
                accept='.docx'
                maxCount={1}
                showUploadList={false}
                beforeUpload={file => {
                  setDocxFile(file);
                  return false;
                }}
                style={{ padding: '4px 0' }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    padding: '12px 0',
                  }}
                >
                  <InboxOutlined style={{ fontSize: 24, color: '#6B7B5A' }} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>
                      上傳標準檔案
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>法務已批注的 .docx</div>
                  </div>
                </div>
              </Dragger>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 14px',
                  background: '#f6f8f4',
                  border: '1px solid #b7c9a8',
                  borderRadius: 8,
                }}
              >
                <PaperClipOutlined style={{ color: '#6B7B5A', fontSize: 16 }} />
                <span style={{ flex: 1, fontSize: 13, color: '#374151', wordBreak: 'break-all' }}>
                  {docxFile.name}
                </span>
                <Button
                  type='text'
                  size='small'
                  icon={<CloseOutlined />}
                  onClick={() => setDocxFile(null)}
                  title='移除檔案'
                />
              </div>
            )}

            <div style={{ color: '#6b7280', fontSize: 13 }}>
              ✓ 系統將自動帶入此次審閱結果（共 {activeResults.length} 筆）作為 AI 結果
            </div>

            <Button
              size='large'
              block
              disabled={!docxFile}
              onClick={handleSubmit}
              style={BTN_STYLE}
            >
              開始
            </Button>
          </Space>
        );

      case 'loading':
        return (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <Spin size='large' />
            <div style={{ marginTop: 16, color: '#6b7280', fontSize: 14 }}>
              處理中，預計需要 30–60 秒...
            </div>
          </div>
        );

      case 'done':
        return (
          <Space direction='vertical' style={{ width: '100%' }} size='middle'>
            <Alert type='success' message='驗測完成！請下載結果檔案。' showIcon />

            {/* 準確率統計一行文字 */}
            {result && (
              <div
                style={{
                  padding: '10px 14px',
                  background: '#f6f8f4',
                  border: '1px solid #b7c9a8',
                  borderRadius: 8,
                  fontSize: 14,
                  color: '#374151',
                  textAlign: 'center',
                  letterSpacing: 0.5,
                }}
              >
                正確率：<strong>{(result.recall ?? 0).toFixed(1)}%</strong>
                　｜ 誤報率：<strong>{(result.false_positive_rate ?? 0).toFixed(1)}%</strong>
              </div>
            )}

            <Button
              icon={<DownloadOutlined />}
              size='large'
              block
              onClick={() => handleDownload('word')}
              style={BTN_STYLE}
            >
              合併 Word（AI + 法務）
            </Button>
            <Button
              icon={<DownloadOutlined />}
              size='large'
              block
              onClick={() => handleDownload('excel')}
              style={BTN_STYLE}
            >
              Excel 統計表
            </Button>
          </Space>
        );

      case 'error':
        return (
          <Space direction='vertical' style={{ width: '100%' }} size='middle'>
            <Alert type='error' message='驗測失敗' description={errorMessage} showIcon />
            <Button size='large' block onClick={reset}>
              重試
            </Button>
          </Space>
        );
    }
  };

  return (
    <Modal
      title='自動驗測'
      open={visible}
      onCancel={handleClose}
      footer={null}
      closable={step !== 'loading'}
      maskClosable={step !== 'loading'}
      width={480}
      destroyOnClose
      afterClose={reset}
    >
      {renderBody()}
    </Modal>
  );
};

export default ValidationModal;
